import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./pagination.css";

const getPageLink = (pageNum, keyword) => {
  if (keyword && keyword.trim() !== "") {
    return `/search/${keyword}/page/${pageNum}`;
  }
  return `/page/${pageNum}`;
};

const getPaginationRange = (page, pages) => {
  let start = Math.max(1, page - 1);
  let end = Math.min(pages, page + 1);
  if (pages > 3) {
    if (page === 1) { start = 1; end = 3; }
    else if (page === pages) { start = pages - 2; end = pages; }
  }
  return { start, end };
};

// ── Skeleton ────────────────────────────────────────────────────
export const PaginationSkeleton = () => (
  <div className="pagination-wrapper pagination-wrapper--skeleton">
    <div className="pagination-inner">
      {[36, 36, 44, 36, 36].map((w, i) => (
        <div
          key={i}
          className="page-btn page-btn--skeleton"
          style={{ width: w }}
        />
      ))}
    </div>
  </div>
);

// ── Real Pagination ──────────────────────────────────────────────
const Pagination = ({ page, pages, keyword = "" }) => {
  const location = useLocation();
  const { start, end } = getPaginationRange(page, pages);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  if (pages <= 1) return null;

  return (
    <div className="pagination-wrapper">
      <div className="pagination-inner">
        {/* Prev */}
        {page > 1 ? (
          <Link className="page-btn page-btn--arrow" to={getPageLink(page - 1, keyword)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        ) : (
          <span className="page-btn page-btn--arrow page-btn--disabled">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        )}

        {/* First page + ellipsis if needed */}
        {start > 1 && (
          <>
            <Link className="page-btn" to={getPageLink(1, keyword)}>1</Link>
            {start > 2 && <span className="page-ellipsis">…</span>}
          </>
        )}

        {/* Page numbers */}
        {[...Array(end - start + 1)].map((_, idx) => {
          const pageNum = start + idx;
          return pageNum === page ? (
            <span key={pageNum} className="page-btn page-btn--active">{pageNum}</span>
          ) : (
            <Link key={pageNum} className="page-btn" to={getPageLink(pageNum, keyword)}>
              {pageNum}
            </Link>
          );
        })}

        {/* Last page + ellipsis if needed */}
        {end < pages && (
          <>
            {end < pages - 1 && <span className="page-ellipsis">…</span>}
            <Link className="page-btn" to={getPageLink(pages, keyword)}>{pages}</Link>
          </>
        )}

        {/* Next */}
        {page < pages ? (
          <Link className="page-btn page-btn--arrow" to={getPageLink(page + 1, keyword)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        ) : (
          <span className="page-btn page-btn--arrow page-btn--disabled">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        )}
      </div>
    </div>
  );
};

export default Pagination;